import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, Typography } from '@mui/material';
import axios from 'axios';
import useSWR from 'swr';
import { apiHost } from '../utils/apiHost';
import { StrukPelanggan } from './StrukPelanggan';
import { StrukTiket } from './StrukTiket';
import { StrukLaci } from './StrukLaci';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true });
const fetcher = (url) => api.get(url).then((r) => r.data);

/**
 * Menu Struk: tab Struk Pelanggan, Tiket Dapur, Tiket Bar, Laci Kasir (pengaturan terpisah per cabang). Semua tab tetap terpasang
 * (hanya disembunyikan) supaya perubahan yang belum disimpan tidak hilang saat pindah tab.
 */
export const Struk = () => {
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.role === 'superadmin';
  const cabangSendiri = user?.cabang?.uuid || user?.cabanguuid || '';
  const [pilihCabang, setPilihCabang] = useState('');
  const [tab, setTab] = useState(0);
  const cabangUuid = isSuperAdmin ? pilihCabang : cabangSendiri;

  const { data: daftarCabang } = useSWR(isSuperAdmin ? '/cabang' : null, fetcher);
  const { data, isLoading, mutate } = useSWR(
    cabangUuid ? `/getkonfigurasicabang?cabangUuid=${cabangUuid}` : null,
    (url) => api.get(url).then((r) => r.data).catch((e) => {
      if (e.response?.status === 404) return { status: true, data: null }; // konfigurasi belum dibuat: dibuat saat menyimpan
      throw e;
    }),
  );
  const { data: cabangData } = useSWR(cabangUuid ? `/cabang/${cabangUuid}` : null, fetcher);

  const konfigurasi = data?.data || null;
  const namaCabang = cabangData?.data?.namacabang || '';
  const bersama = { cabangUuid, konfigurasi, namaCabang, mutate };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Struk</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Atur isi, lebar, dan urutan struk pelanggan serta tiket dapur dan bar. Pratinjau di kanan sama persis dengan yang dicetak
        kasir web dan aplikasi HP. Struk shift hanya ikut lebar kertas struk pelanggan.
      </Typography>

      {isSuperAdmin && (
        <FormControl size="small" sx={{ minWidth: 260, mb: 2 }}>
          <InputLabel>Cabang</InputLabel>
          <Select label="Cabang" value={pilihCabang} onChange={(e) => setPilihCabang(e.target.value)}>
            {(daftarCabang?.data || []).map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      {!cabangUuid ? (
        <Alert severity="info">{isSuperAdmin ? 'Pilih cabang untuk mengatur struknya.' : 'Akun ini tidak terhubung ke cabang.'}</Alert>
      ) : isLoading ? (
        <CircularProgress size={28} />
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }} variant="scrollable" scrollButtons="auto">
            <Tab label="Struk Pelanggan" />
            <Tab label="Tiket Dapur" />
            <Tab label="Tiket Bar" />
            <Tab label="Laci Kasir" />
          </Tabs>
          <Box hidden={tab !== 0}><StrukPelanggan {...bersama} /></Box>
          <Box hidden={tab !== 1}><StrukTiket jenis="dapur" {...bersama} /></Box>
          <Box hidden={tab !== 2}><StrukTiket jenis="bar" {...bersama} /></Box>
          <Box hidden={tab !== 3}><StrukLaci {...bersama} /></Box>
        </>
      )}
    </Box>
  );
};
